import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UpperCasePipe } from '@angular/common';
import { IncidentEditFormType, IncidentMessageFormType } from './incident-edit-form';
import { FilterDTO, FilterOperator, PagedResultDTOOfServiceClientDTO, ServiceClientClient, ServiceClientDTO, ServiceStatusLevel, SortDirection } from 'src/app/base/models/models.swagger.generated';
import { DropDownItem } from 'src/app/base/shared/drop-down-item.model';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { TabModule } from '@syncfusion/ej2-angular-navigations';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { MultiSelectFilterComponent } from 'src/app/base/shared/multi-select-filter/multi-select-filter.component';
import { MultiSelectFilterType } from 'src/app/base/shared/multi-select-filter/multi-select-filter-type';
import { MultiSelectItem } from 'src/app/base/shared/multi-select-filter/multi-select-item';
import { Observable, Subject, catchError, distinctUntilChanged, map, of, switchMap, takeUntil } from 'rxjs';
import { DropDownConstantsFactory } from 'src/app/base/shared/syncfusion/dropdown-constants-factory';
import { MarkAsteriskDirective } from 'src/app/base/directives/mark-asterisk.directive';

@Component({
  selector: 'app-incident-edit-form',
  templateUrl: './incident-edit-form.component.html',
  styleUrls: ['./incident-edit-form.component.scss'],
  imports: [ReactiveFormsModule, TranslateModule, UpperCasePipe, DropDownListModule, TabModule, CheckBoxModule, MultiSelectFilterComponent, MarkAsteriskDirective]
})
export class IncidentEditFormComponent implements OnInit, OnDestroy {
  @Input() incidentEditForm: FormGroup<IncidentEditFormType>;
  @Input() serviceDropdownItems: DropDownItem[] = [];
  @Input() isReadOnly = false;
  @Output() languagesChanged = new EventEmitter<string[]>();

  public statusOptions: { value: number; text: string }[] = [];
  public dropdownFields = { text: 'text', value: 'value' };
  public serviceDropdownFields = DropDownConstantsFactory.createEnumDropdownSettings();

  public MultiSelectFilterType = MultiSelectFilterType;
  public selectedClientIds: number[] = [];

  public tabAnimation = {
    previous: { effect: '', duration: 0, easing: '' },
    next: { effect: '', duration: 0, easing: '' },
  };

  private destroy$ = new Subject<void>();
  private selectedClientIds$ = new Subject<number[]>();

  constructor(
    private serviceClientClient: ServiceClientClient,
    private translateService: TranslateService
  ) {}

  public ngOnInit(): void {
    this.statusOptions = [
      { value: ServiceStatusLevel.Operational, text: this.translateService.instant('i18n.domain.status.levels.operational') },
      { value: ServiceStatusLevel.Degraded, text: this.translateService.instant('i18n.domain.status.levels.degraded') },
      { value: ServiceStatusLevel.Down, text: this.translateService.instant('i18n.domain.status.levels.down') }
    ];

    // Reset selectedClientIds when sendNotification is unchecked
    this.incidentEditForm.controls.sendNotification.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(sendNotification => {
        if (!sendNotification) {
          this.selectedClientIds = [];
        }
      });

    this.selectedClientIds$
      .pipe(
        map(ids => [...(ids ?? [])].sort((a, b) => a - b)),
        distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
        switchMap(ids => {
          if (ids.length === 0) {
            return of<string[]>([]);
          }
          return this.serviceClientClient.getAdminLanguagesByClientIds(ids)
            .pipe(catchError(() => of<string[]>([])));
        }),
        map(languages => Array.from(new Set((languages ?? []).filter(language => !!language)))),
        distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
        takeUntil(this.destroy$)
      )
      .subscribe(languages => {
        this.languagesChanged.emit(languages);
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get messagesArray(): FormArray<FormGroup<IncidentMessageFormType>> {
    return this.incidentEditForm.controls.messages;
  }

  public onSelectedClientsChanged(ids: number[]): void {
    this.selectedClientIds = ids;
    this.incidentEditForm.controls.clientIds.setValue(ids);
    this.selectedClientIds$.next(ids ?? []);
  }

  public searchClients(searchText: string): Observable<MultiSelectItem[]> {
    const filters: FilterDTO[] = [];
    if (searchText) {
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'Name',
        operator: FilterOperator.Contains,
        value: searchText,
      }));
    }
    return this.serviceClientClient.searchServiceClients(0, 20, 'Name', SortDirection.Ascending, filters)
      .pipe(map((result: PagedResultDTOOfServiceClientDTO) => {
        const items = result?.items ?? [];
        return items.map(client => ({
          id: client.id,
          name: client.name
        } as MultiSelectItem));
      }));
  }
}
